import { HotDeal, IRogue, Product } from '@/js/services/RogueTypes'
import { CUSTOM_BUMPER_PRODUCT_SKUS, DYNAMIC_IMG_BY_SKU, LOCKED } from './productConstants'
import { usePromoStore } from './promoStore' // eslint-disable-line import/no-cycle
import { useListStore } from './listStore' // eslint-disable-line import/no-cycle

export function updateWithPromoPricing(rogue:IRogue, product:Product) {
  if (!product) {
    return
  }

  const promoStore = usePromoStore(rogue.pinia)
  const promo: HotDeal | undefined = promoStore.activePromos[product.productId]
  const customerPrice: number | undefined = promoStore.customerPrices[product.productId]

  const attrs = product.attributes

  // product.attributes is NOT reactive - we dont want to observe the whole array object. BUT, they are necessary
  // to update so when we put this product through the univeral price function, it will return the right value.
  if (product.type === 'simple') {
    if (promo && promo.status === 'active') {
      if (!product.hasPromo) {
        rogue.log.debug(`Updating promo price for ${product.name} ${product.productId}`, promo)
      }
      attrs.rogue_promo_enabled = true
      product.hasPromo = true

      if (promo.price) {
        attrs.rogue_promo_price = promo.price
      }

      if (promo.qty !== undefined && promo.qty !== null) {
        product.inventory.promoQty = promo.qty
      }
    } else {
      const hadPromo = product.hasPromo
      // We have to set these to null so Vue can observe it if it changes later
      attrs.rogue_promo_enabled = false
      product.inventory.promoQty = null
      product.hasPromo = false
      if (hadPromo) {
        rogue.log.debug(`Removing promo price for ${product.name}`, promo)
        const listStore = useListStore(rogue.pinia)
        listStore.handleDeactivatedPromo(product, rogue)
      }
    }
  } else {
    product.hasPromo = false
  }

  if (customerPrice && (!attrs.special_price || customerPrice < Number(attrs.special_price))) {
    // Save the original special price so we can restore it if the customer logs out
    if (attrs.original_special_price === undefined) {
      attrs.original_special_price = attrs.special_price || ''
    }
    attrs.special_price = `${customerPrice}`
  } else if (!customerPrice && attrs.original_special_price !== undefined) {
    // Restore the original special price
    if (attrs.original_special_price) {
      attrs.special_price = attrs.original_special_price
    } else {
      delete attrs.special_price
      delete attrs.original_special_price
    }
  }

  product.special_price = attrs.special_price ? Number(attrs.special_price) : null
}

/**
 * Ideally, the m2-content server would return products exactly how SPA wants. But it's easier to modify the response
 * than to change the backend and frontend, so this function is called for each product to change or add properties
 * to fit with what SPA wants.
 * It's often better to do this processing once - when the product is loaded from the server - than have to re-do
 * it in all the different components that use the products
 * @param data
 * @param rogue
 */
export function prepareProduct(data: Product, rogue: IRogue): void {
  if (data.gallery) {
    data.gallery
      .filter(entry => entry.label?.find(l => l.indexOf('dynamic_') === 0))
      .forEach(entry => entry.originalLink = entry.link)
  }

  const attributes = data.attributes || {}

  if (data.type === 'virtual' && Boolean(attributes.program_delivery) && attributes.program_delivery !== 'N/A') {
    data.isProgram = true
    data.inventory.max = 1
  }

  if (data.relatedIds?.length && !data.relatedIds.includes(data.productId)) {
    data.relatedIds.unshift(data.productId)
  }

  // if (rogue.l10n.path) {
  //   data.originalUrl = data.url
  //   data.url = rogue.l10n.path + data.url
  // }

  if (attributes.short_description) {
    let short = attributes.short_description
      .replace(/<\/?span>/g, '')
      .replace(/<\/?strong>/g, '')
      .replace(/<\/?p>/g, ' ')
      .replace(/<\/?div>/g, ' ')
      .replace(/\r?\n/g, ' ')
      .replace(/\s\s+/g, ' ')
      .trim()

    if (short.indexOf('<vue-image') !== -1 || short.indexOf('<style') !== -1) {
      short = ''
    }

    attributes.short_description = short
  }

  if (typeof attributes.shipperhq_shipping_group === 'string') {
    attributes.shipperhq_shipping_group = (attributes.shipperhq_shipping_group as string).split(',')
  }

  if (attributes.shipperhq_shipping_group) {
    attributes.shipperhq_shipping_group.sort()
  }

  if (LOCKED[data.sku]) {
    attributes.ticket_required = LOCKED[data.sku]
  }

  if (DYNAMIC_IMG_BY_SKU[data.sku]) {
    data.gallery = DYNAMIC_IMG_BY_SKU[data.sku].map((config, i) => ({
      label       : [`dynamic_${i}`],
      link        : '',
      root        : config.folder,
      wOpts       : config.options,
      type        : 'image',
      alt         : '',
      isInGallery : true,
    }))
  }

  if (data.sku.indexOf('MMV') === 0) {
    data.inventory.max = 1
  }

  // if (data.productId === 46859) {
  //   data.dynamicImageRoot = '/dynamic/storage_test_4/'
  //   if (data.gallery?.length) {
  //     data.gallery[0].label = ['dynamic_image']
  //     data.gallery[0].link = ''
  //   }
  // }

  // Hiding the "Made in USA" row in gear specs and badge under title for all boneyard products - Task EC-3020
  if (attributes.rogue_boneyard === true && attributes.made_in_usa_custom === 'Hidden') {
    delete attributes.made_in_usa
  }

  // Add bundle options to childrenIds to simplify loading
  if (data.bundleOptions) {
    data.childrenIds = data.childrenIds || []
    data.bundleOptions.forEach(opt => {
      if (opt.includedProductId) {
        data.childrenIds?.push(opt.includedProductId)
      }
      opt.values.forEach(val => {
        data.childrenIds?.push(val.productId)
      })
    })
  }

  const bg = data.roguebuilder?.props?.background
  if (bg && bg !== 'gray' && !data.gallery?.length) {
    data.gallery = [{ label: ['dynamic_image'], link: '', type: 'image', alt: '', isInGallery: true }]
  }

  if (CUSTOM_BUMPER_PRODUCT_SKUS[data.sku]) {
    data.isCustomBumper = true
    data.attributes.rogue_children_qty_zero = true
  }

  let ALL_SHIP_DIRECT: string[] = []

  ALL_SHIP_DIRECT = ALL_SHIP_DIRECT.concat(rogue.site.directShip || [])
    .concat(rogue.site.heavyDirect || [])

  if (ALL_SHIP_DIRECT.length) {
    const itemGroups = attributes.shipperhq_shipping_group || []
    data.shipDirect = ALL_SHIP_DIRECT.find(group => itemGroups.indexOf(group) !== -1)
  }

  // @todo this should move into m2-content
  if (data.visibility === 1) {
    Object.assign(data.attributes, {
      search_keywords: undefined,
    })
  }

  // Check if we can use the size of the hero to determine the gallery height for mobile
  if (data.roguebuilder && data.roguebuilder.props?.layout === 'hero') {
    if (!data.roguebuilder.slots.hero) {
      // Fallback in case there is no hero block defined... we dont want to get here
      data.roguebuilder.slots.hero = [{ name: 'hero', props: { productImage: 'useFirst' } }]
    }

    const props = data.roguebuilder.slots.hero?.[0]?.props
    if (props.useProductImage && !props.productImage) {
      props.productImage = 'useFirst'
    }

    if (!props.background && data.gallery?.length) {
      // Fallback in case the background wasnt set. This will probably cause clipping...
      props.background = { src: data.gallery[0].link, width: 1600, height: 900 }
    }

    data.heroStyle = props.productImage

    // Set the width and height of the first image in the gallery, so that Mobile will show the gallery at the Hero height
    if (data.gallery?.length === 1) {
      const { productImage, background } = props || {}

      if (productImage === 'useFirst' && background) {
        data.gallery[0].width = background.width
        data.gallery[0].height = background.height
      }
    }
  }

  if (data.roguebuilder && data.roguebuilder.props?.relatedIds) {
    data.relatedIds = data.roguebuilder.props.relatedIds
  }

  const rhpaThumbnail = data.gallery?.find(img => img.label.includes('rhpathumb'))
  if (rhpaThumbnail && data.gallery) {
    // Remove the RHPA thumb from the gallery
    data.gallery = data.gallery.filter(g => g !== rhpaThumbnail)
    // Set the RHPA thumb to the top level property, for use in Configuration.vue
    data.rhpaThumbnail = rhpaThumbnail.link
  }

  const rhpaBundleThumbnail = data.gallery?.find(img => img.label.includes('rhpathumb_bundle'))
  if (rhpaBundleThumbnail && data.gallery) {
    // Remove the RHPA thumb from the gallery
    data.gallery = data.gallery.filter(g => g !== rhpaBundleThumbnail)
    // Set the RHPA thumb to the top level property, for use in Configuration.vue
    data.rhpaBundleThumbnail = rhpaBundleThumbnail.link
  }

  // We dont need Vue to turn these into observable attributes, since they wont change
  // https://github.com/vuejs/vue/issues/2637#issuecomment-361633222
  if (Object.defineProperty) {
    Object.defineProperty(data, 'roguebuilder', { configurable: false })
    Object.defineProperty(data, 'attributes', { configurable: false })
  }

  if (data.attributes.is_registration_sku === true) {
    data.hasRegistration = true
    data.inventory = { max: 1, min: 1, ms: true, bin: false, promoQty: null }
  }

  updateWithPromoPricing(rogue, data)
}
